import React from 'react';
import { UnitSystem } from '../types/event';
import {
  formatAltitude,
  formatSpeed,
  formatVerticalSpeed,
  formatDistanceKm,
  headingToCompass,
} from '../utils/formatters';

interface StatsDrawerProps {
  eventTitle: string;
  eventType: string;
  isOpen: boolean;
  distance: number;
  currentSpeed: number;
  currentAltitude: number | null;
  currentHeartRate: number | null;
  verticalSpeedMps: number | null;
  flightPhase: string | null;
  headingDegNow: number | null;
  unitSystem: UnitSystem;
  onUnitSystemChange: (u: UnitSystem) => void;
}

const StatsDrawer: React.FC<StatsDrawerProps> = ({
  eventTitle,
  eventType,
  isOpen,
  distance,
  currentSpeed,
  currentAltitude,
  currentHeartRate,
  verticalSpeedMps,
  flightPhase,
  headingDegNow,
  unitSystem,
  onUnitSystemChange,
}) => {
  const isFlight = eventType === 'flight';
  const displayUnit = isFlight ? unitSystem : 'metric';

  return (
    <div className={`side-drawer side-drawer-right${isOpen ? '' : ' drawer-closed'}`}>
      <div className="drawer-inner">
        <div className="stats-panel-header">
          <h2>{eventTitle}</h2>
        </div>

        {isFlight && (
          <div className="unit-toggle" role="group" aria-label="Units">
            <button
              type="button"
              className={`unit-toggle-btn ${unitSystem === 'imperial' ? 'active' : ''}`}
              onClick={() => onUnitSystemChange('imperial')}
              title="Imperial units"
            >
              Imperial
            </button>
            <button
              type="button"
              className={`unit-toggle-btn ${unitSystem === 'metric' ? 'active' : ''}`}
              onClick={() => onUnitSystemChange('metric')}
              title="Metric units"
            >
              Metric
            </button>
          </div>
        )}

        <div className="stat-item">
          <div className="stat-label">Distance</div>
          <div className="stat-value">{formatDistanceKm(distance, displayUnit)}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Speed</div>
          <div className="stat-value">{formatSpeed(currentSpeed, displayUnit)}</div>
        </div>

        {isFlight && (
          <div className="stat-item">
            <div className="stat-label">Altitude</div>
            <div className="stat-value">
              {currentAltitude !== null ? formatAltitude(currentAltitude, unitSystem) : '—'}
            </div>
          </div>
        )}

        {isFlight && (
          <div className="stat-item">
            <div className="stat-label">Vertical Speed</div>
            <div className="stat-value">
              {verticalSpeedMps !== null ? formatVerticalSpeed(verticalSpeedMps, unitSystem) : '—'}
            </div>
          </div>
        )}

        {isFlight && (
          <div className="stat-item">
            <div className="stat-label">Phase</div>
            <div className="stat-value">{flightPhase ?? '—'}</div>
          </div>
        )}

        {isFlight && (
          <div className="stat-item">
            <div className="stat-label">Heading</div>
            <div className="stat-value">
              {headingDegNow === null
                ? '—'
                : `${headingDegNow.toFixed(0)}° ${headingToCompass(headingDegNow)}`}
            </div>
          </div>
        )}

        {currentHeartRate !== null && (
          <div className="stat-item">
            <div className="stat-label">Heart Rate</div>
            <div className="stat-value">{currentHeartRate} bpm</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsDrawer;
